import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import AuthShell from "./components/AuthShell";

const jobTitles = [
  "Operations Manager",
  "Dispatcher",
  "Office Admin",
  "Crew Lead",
  "Owner",
  "Other",
];

export default function SignupPage() {
  const navigate = useNavigate();
  const { signUp, signIn } = useAuth();

  const [fullName, setFullName] = useState("");
  const [jobTitle, setJobTitle] = useState("Operations Manager");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) return setError("Please enter your name.");
    if (!email.trim()) return setError("Please enter your email.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    if (!agree) return setError("Please accept the portal access policy to continue.");

    setLoading(true);
    const { error: err } = await signUp({
      email: email.trim(),
      password,
      fullName: fullName.trim(),
      jobTitle,
    });
    if (err) {
      setLoading(false);
      setError(err);
      return;
    }
    // Try sign-in right away (works when email confirmations are disabled in Supabase)
    const { error: signInErr } = await signIn(email.trim(), password);
    setLoading(false);
    if (signInErr) {
      // Likely email confirmation required — send them to login with a friendly note
      navigate("/login", { replace: true });
      return;
    }
    navigate("/dashboard", { replace: true });
  };

  return (
    <AuthShell
      title="Create your portal account."
      subtitle="Set up a Haulmark portal account in under a minute."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-brand-600 hover:text-brand-700 cursor-pointer"
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 flex items-start gap-2">
            <i className="ri-error-warning-line text-base mt-0.5 shrink-0"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider">
              Full name
            </label>
            <input
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Elena Cordero"
              className="w-full text-sm px-3 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider">
              Job title
            </label>
            <div className="relative">
              <select
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full text-sm pl-3 pr-9 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors appearance-none cursor-pointer"
              >
                {jobTitles.map((j) => (
                  <option key={j} value={j}>
                    {j}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"></i>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider">
            Work email
          </label>
          <div className="relative">
            <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
              <i className="ri-mail-line text-sm"></i>
            </span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@haulmark.co"
              className="w-full text-sm pl-10 pr-3 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider">
              Password
            </label>
            <div className="relative">
              <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
                <i className="ri-lock-2-line text-sm"></i>
              </span>
              <input
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full text-sm pl-10 pr-10 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-ink-400 hover:text-ink-700 cursor-pointer"
              >
                <i className={showPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider">
              Confirm
            </label>
            <input
              type={showPwd ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat password"
              className="w-full text-sm px-3 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
            />
          </div>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer select-none text-sm text-ink-600">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-ink-300 text-brand-500 focus:ring-brand-400 cursor-pointer"
          />
          <span>
            I&apos;m authorized to access Haulmark&apos;s operations data and agree to the team
            portal access policy.
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Creating account…
            </>
          ) : (
            <>
              Create portal account
              <i className="ri-arrow-right-line"></i>
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}